import { useEffect, useState } from 'react';
import { api } from './api';
import { formatInActiveTimeZone } from './timezone';
const labels: Record<string, string> = {
  MICROSOFT: 'Microsoft',
  GOOGLE: 'Google',
  GITHUB: 'GitHub',
  APPLE: 'Apple',
  LOCAL_PASSWORD: 'Email/password',
};
// Unmodified provider-distributed artwork; see public/identity/README.md.
const providerArtwork: Record<string, { src: string; label: string }> = {
  MICROSOFT: { src: '/identity/microsoft-signin.png', label: 'Sign in with Microsoft' },
  GOOGLE: { src: '/identity/google-signin.png', label: 'Sign in with Google' },
  APPLE: { src: '/identity/apple-continue.png', label: 'Continue with Apple' },
};
export function FederatedButtons({ invitationToken }: { invitationToken?: string }) {
  const [providers, setProviders] = useState<{ provider: string; configured: boolean }[]>([]),
    [message, setMessage] = useState('');
  useEffect(() => {
    void api<any>('/v1/auth/federation/providers')
      .then((v) => setProviders(v.items))
      .catch(() => undefined);
  }, []);
  if (!providers.some((p) => p.configured)) return null;
  return (
    <div className="federated-buttons">
      {providers
        .filter((p) => p.configured)
        .map((p) => (
          <button
            className="button secondary provider-signin"
            type="button"
            key={p.provider}
            onClick={() => {
              void api<any>('/v1/auth/federation/start', {
                method: 'POST',
                body: JSON.stringify({
                  provider: p.provider,
                  ...(invitationToken ? { invitationToken } : {}),
                }),
              })
                .then((v) => window.location.assign(v.authorizationUrl))
                .catch((e) => setMessage(e.message));
            }}
          >
            {providerArtwork[p.provider] ? (
              <img
                src={providerArtwork[p.provider]!.src}
                alt={providerArtwork[p.provider]!.label}
              />
            ) : (
              <>Continue with {labels[p.provider]}</>
            )}
          </button>
        ))}
      {message && <p role="alert">{message}</p>}
      <p>
        New to OpsAI? A sign-in method can create your account. Workspace access requires an
        invitation.
      </p>
      <p className="signin-separator">or continue with email</p>
    </div>
  );
}
export function SignInMethods({ role }: { role: string }) {
  const [data, setData] = useState<any>(null),
    [policy, setPolicy] = useState<any>(null),
    [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false),
    [confirmed, setConfirmed] = useState(false);
  const load = () => api<any>('/v1/auth/federation/methods').then(setData);
  useEffect(() => {
    void load().catch(() => undefined);
    if (['tenant_owner', 'aot_admin'].includes(role))
      void api<any>('/v1/auth/federation/policy')
        .then(setPolicy)
        .catch(() => undefined);
  }, [role]);
  if (!data) return null;
  return (
    <section className="security-section access-panel">
      <h2>Sign-in methods</h2>
      <p>Connecting a provider does not grant workspace access. OpsAI MFA is always required.</p>
      {message && <p role="status">{message}</p>}
      <div className="access-cards">
        <article>
          <h3>Password</h3>
          <p>{data.passwordEnabled ? 'Enabled' : 'Not enabled'}</p>
          <p>Only you can choose or reset your password through account recovery.</p>
        </article>
        {data.providers.map((p: any) => {
          const connected = data.items.find((i: any) => i.provider === p.provider);
          return (
            <article key={p.provider}>
              <h3>{labels[p.provider]}</h3>
              <p>
                {connected
                  ? 'Connected'
                  : p.configured
                    ? 'Not connected'
                    : 'Not yet available from AOT'}
              </p>
              {connected && (
                <>
                  <p className="access-email">{connected.email ?? 'Email not provided'}</p>
                  <p>
                    Last used:{' '}
                    {connected.lastUsedAt
                      ? formatInActiveTimeZone(connected.lastUsedAt)
                      : 'Not yet used'}
                  </p>
                </>
              )}
              <button
                className="button secondary"
                disabled={busy || (!connected && !p.configured)}
                onClick={() => {
                  setBusy(true);
                  setMessage('');
                  void api<any>(
                    connected ? '/v1/auth/federation/unlink' : '/v1/auth/federation/link',
                    { method: 'POST', body: JSON.stringify({ provider: p.provider }) },
                  )
                    .then(async (v) => {
                      if (v.authorizationUrl) window.location.assign(v.authorizationUrl);
                      else {
                        await load();
                        setMessage('Sign-in method disconnected.');
                      }
                    })
                    .catch((e) => setMessage(e.message))
                    .finally(() => setBusy(false));
                }}
              >
                {connected ? 'Disconnect' : 'Connect'}
              </button>
            </article>
          );
        })}
      </div>
      {policy && (
        <form
          className="access-form"
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            void api('/v1/auth/federation/policy', {
              method: 'PUT',
              body: JSON.stringify(policy.policy),
            })
              .then((result: any) => {
                setPolicy({ ...policy, policy: result.policy });
                setMessage('Authentication policy updated. MFA remains required.');
                setConfirmed(false);
              })
              .catch((e) => setMessage(e.message))
              .finally(() => setBusy(false));
          }}
        >
          <h2>Authentication</h2>
          <p>
            MFA is required for every role. Every active owner must retain an allowed sign-in
            method.
          </p>
          <label>
            Sign-in policy
            <select
              value={policy.policy.mode ?? 'RESTRICTED'}
              onChange={(e) => {
                const defaults = e.target.value === 'PLATFORM_DEFAULT';
                setConfirmed(false);
                setPolicy({
                  ...policy,
                  policy: {
                    ...policy.policy,
                    mode: e.target.value,
                    ...(defaults
                      ? {
                          allowedMethods: [
                            'LOCAL_PASSWORD',
                            ...data.providers
                              .filter((p: any) => p.configured)
                              .map((p: any) => p.provider),
                          ],
                          microsoftTenantId: null,
                          googleWorkspaceDomain: null,
                        }
                      : {}),
                  },
                });
              }}
            >
              <option value="PLATFORM_DEFAULT">Use platform defaults</option>
              <option value="RESTRICTED">Restrict sign-in methods</option>
            </select>
          </label>
          <p>
            Platform defaults allow every sign-in method configured by AOT, including methods added
            later. Workspace access still requires a membership.
          </p>
          <fieldset>
            <legend>Allowed sign-in methods</legend>
            {['LOCAL_PASSWORD', ...data.providers.map((p: any) => p.provider)].map(
              (method: string) => (
                <label className="toggle-row" key={method}>
                  <span>{labels[method]}</span>
                  <input
                    type="checkbox"
                    checked={policy.policy.allowedMethods.includes(method)}
                    disabled={
                      policy.policy.mode === 'PLATFORM_DEFAULT' ||
                      (method !== 'LOCAL_PASSWORD' &&
                        !data.providers.find((p: any) => p.provider === method)?.configured)
                    }
                    onChange={(e) =>
                      setPolicy({
                        ...policy,
                        policy: {
                          ...policy.policy,
                          allowedMethods: e.target.checked
                            ? [...policy.policy.allowedMethods, method]
                            : policy.policy.allowedMethods.filter((m: string) => m !== method),
                        },
                      })
                    }
                  />
                </label>
              ),
            )}
          </fieldset>
          <label>
            Microsoft Entra tenant ID (optional)
            <input
              disabled={policy.policy.mode === 'PLATFORM_DEFAULT'}
              value={policy.policy.microsoftTenantId ?? ''}
              onChange={(e) =>
                setPolicy({
                  ...policy,
                  policy: { ...policy.policy, microsoftTenantId: e.target.value || null },
                })
              }
              placeholder="Organizational tenant GUID"
            />
          </label>
          <label>
            Google Workspace domain (optional)
            <input
              disabled={policy.policy.mode === 'PLATFORM_DEFAULT'}
              value={policy.policy.googleWorkspaceDomain ?? ''}
              onChange={(e) =>
                setPolicy({
                  ...policy,
                  policy: {
                    ...policy.policy,
                    googleWorkspaceDomain: e.target.value.toLowerCase() || null,
                  },
                })
              }
              placeholder="example.com"
            />
          </label>
          <label className="toggle-row">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <span>
              I understand that these restrictions apply to existing sessions and future sign-ins.
            </span>
          </label>
          <button className="button primary" disabled={busy || !confirmed}>
            Save authentication policy
          </button>
        </form>
      )}
    </section>
  );
}
